import { useSuspenseQuery } from '@apollo/client/react';
import { GET_MANAGER } from './graphql/queries.ts';
import {
  GetManagerQuery,
  GetManagerQueryVariables,
  Manager,
  ManagerError,
  Managerrechte,
  ManagerSettings,
} from '../gql/graphql.ts';
import { handleGraphQLErrors } from '../utils/errors/handleGraphQLErrors.ts';
import * as Sentry from '@sentry/react';

export class ManagerProfile {
  constructor(
    readonly id: string,
    readonly standortId: string,
    readonly vorname: string,
    readonly nachname: string,
    readonly email: string,
    readonly rechte: Managerrechte,
    readonly settings: ManagerSettings,
  ) {}

  hasAbrechnungsRecht(): boolean {
    return (
      this.rechte.abrechnung.export || this.rechte.abrechnung.monatsabschluss
    );
  }

  hasAuswertungsRecht(): boolean {
    return (
      this.rechte.auswertung.arbeitszeit ||
      this.rechte.auswertung.dienstplan ||
      this.rechte.auswertung.personal
    );
  }

  hasControllingRecht(): boolean {
    return this.hasAuswertungsRecht() || this.rechte.tagebuch;
  }

  hasDienstplanReadOnlyRecht(): boolean {
    return (
      this.rechte.dienstplan.erlaubt &&
      !this.rechte.dienstplan.schichtvorlagen &&
      !this.rechte.dienstplan.bearbeiten &&
      !this.rechte.dienstplan.freigabe &&
      !this.rechte.dienstplan.mitarbeiterEinteilen
    );
  }

  hasUnrestrictedMitarbeiterRechte(): boolean {
    return (
      this.rechte.mitarbeiter.erlaubt && !this.rechte.mitarbeiter.eingeschraenkt
    );
  }

  // Würde ich mal disablen, da ich grad nicht sehe wie wir das vereinfachen können
  // eslint-disable-next-line complexity
  hasEinstellungenRecht(): boolean {
    return (
      this.rechte.einstellungen &&
      (this.rechte.tagebuch ||
        (this.rechte.mitarbeiter.erlaubt &&
          !this.rechte.mitarbeiter.eingeschraenkt) ||
        this.rechte.arbeitszeit.erlaubt ||
        this.rechte.kalender ||
        this.hasAbrechnungsRecht() ||
        (this.rechte.dienstplan.erlaubt && !this.hasDienstplanReadOnlyRecht()))
    );
  }

  isPersonalAuswerter(): boolean {
    return (
      !this.rechte.auswertung.dienstplan && this.rechte.auswertung.personal
    );
  }

  isArbeitszeitAuswerter(): boolean {
    return (
      !this.rechte.auswertung.dienstplan &&
      !this.rechte.auswertung.personal &&
      this.rechte.auswertung.arbeitszeit
    );
  }

  isDienstplanAdmin(): boolean {
    return this.rechte.dienstplan.erlaubt && !this.hasDienstplanReadOnlyRecht();
  }
}

export function useProfile(): { manager?: ManagerProfile; error?: string } {
  const { data } = useSuspenseQuery<GetManagerQuery, GetManagerQueryVariables>(
    GET_MANAGER,
  );

  const errors = handleGraphQLErrors<Pick<Manager, '__typename'>, ManagerError>(
    data.manager,
  );

  if (errors) {
    Sentry.captureException(errors.message);
    throw new Error(errors.message);
  }

  const manager = data.manager as Manager;
  return {
    manager: new ManagerProfile(
      manager.id,
      manager.idStandort,
      manager.vorname,
      manager.nachname,
      manager.email,
      manager.managerrechte,
      manager.settings,
    ),
  };
}
